import { AfterContentInit, Component, ContentChildren, forwardRef, Input, Optional, QueryList, ViewChild } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  FormGroupDirective,
  NG_VALUE_ACCESSOR,
  Validators
} from '@angular/forms';
import {
  DynamicControlComponent
} from '../dynamic-control/dynamic-control.component';

@Component({
  selector: 'multicontrol',
  templateUrl: './multicontrol.component.html',
  styleUrls: ['./multicontrol.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MulticontrolComponent),
      multi: true,
    }
  ]
})
export class MulticontrolComponent implements ControlValueAccessor, AfterContentInit{
  @Input() label?: string;
  @Input() formControl: AbstractControl;
  @Input() controls: any;
  @Input() readonly = false;
  innerForm: FormGroup<Record<number|string, any>> = this.fb.group({});
  valueArray: any[][] = [];
  isDisabled: boolean = false;
  isAdditionMode = false;
  editedIndex: number | null = null;
  private _innerControlKeys: string[] = [];
  private _diplayedColumnsDef: string[] = [];
  private _displayedColumnNames: string[] = [];
  private _onChange: (value: any) => void;
  private _onTouched: () => void;

  get displayedColumns(){
    return this._diplayedColumnsDef;
  }

  get displayedColumnNames() {
    return this._displayedColumnNames;
  }
  @ContentChildren(DynamicControlComponent) innerControls!: QueryList<DynamicControlComponent>;
  @ViewChild('ngForm') innerFormDirective: FormGroupDirective;
  constructor(
    private fb: FormBuilder,
    @Optional() private parentForm: FormGroupDirective
  ) {  }

  ngAfterContentInit(): void {
    this.innerControls.forEach((innerControl, idx) => {
      const formControlName = idx.toString();
      if (formControlName) {
        innerControl.formControl.setValidators([Validators.required]);
        innerControl.formControl.updateValueAndValidity();
        innerControl.formControl.markAsPristine();
        innerControl.formControl.markAsTouched();
        this.innerForm.addControl(formControlName, innerControl.formControl);
        this._innerControlKeys.push(innerControl.controlName);
        this._diplayedColumnsDef.push(idx.toString());
        this._displayedColumnNames.push(innerControl.label ?? innerControl.controlName ?? idx.toString())
      }
    });
    this.innerForm.updateValueAndValidity();
  }

  writeValue(value: {id: string | number, value: any}[][]) {
    if (value) {
      setTimeout(() => {
        this.valueArray = value.map(row => this.mapValueToInnerForm(row));
      }, 1)
    }
  }

  registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  private mapInnerFormToValue(data: Record<number|string, any>): Record<string, any>[] {
    return (Object.keys(data)).map((key, idx) => ({
      id: this._innerControlKeys[idx],
      value: data[key]
    }))
  }

  private mapValueToInnerForm(value: {id: string | number, value: any}[]) {
    return this._innerControlKeys.map(id => value.find(element => element.id === id) ?? {id, value: null})
  }

  addData() {
    if (this.innerForm.valid) {
      const data = this.innerForm.value;
      if(this.editedIndex !== null) {
        this.valueArray = this.valueArray.map((value, idx) => idx === this.editedIndex ? this.mapInnerFormToValue(data) : value);
      }
      else {
        this.valueArray = [...this.valueArray, this.mapInnerFormToValue(data)];
      }
      this.quitAdditionMode();
      this._onChange(this.valueArray);
    }
  }

  removeData() {
    this._onTouched();
    this.valueArray = this.valueArray.filter((_, idx) => idx !== this.editedIndex);
    this.quitAdditionMode();
    this._onChange(this.valueArray);
  }

  editData(index: number) {

    if (this.readonly) {
      return;
    }

    const currentValue = this.valueArray[index];
    const newValue = this._innerControlKeys.reduce((acc, id, idx) => {
      acc[idx] = currentValue.find(v => v.id === id).value;
      return acc;
    }, {} as Record<number|string, any>);
    this.innerForm.patchValue(newValue)
    this.editedIndex = index;
    this.enterAdditionMode();
  }

  getValue(element: any, key: string) {
    return element[key]?.value ?? '';
  }

  enterAdditionMode() {
    this.isAdditionMode = true;
    this.innerForm.markAllAsTouched();
  }

  quitAdditionMode() {
    this.isAdditionMode = false;
    this.editedIndex = null;
    this.innerForm.reset();
    this.innerForm.markAsPristine();
    this._onTouched();
  }

  shouldDisplayErrors(): boolean {
    return (this.formControl.invalid && (
      this.formControl.touched ||
      this.formControl.dirty ||
      this.isFormSubmitted()
    )) ?? false;
  }

  private isFormSubmitted(): boolean {
    return this.parentForm?.submitted ?? false;
  }
}
